import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const publicInstance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

const instance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

export default publicInstance;
export const axiosPrivate = instance;

let currentAuth = {};
let setAuthState = null;
let refreshRequest = null;

export const syncAuthSession = (auth, setAuth) => {
  currentAuth = auth ?? {};
  setAuthState = setAuth;
};

const updateAuthSession = (nextAuth) => {
  currentAuth = nextAuth;

  if (setAuthState) {
    setAuthState(nextAuth);
  }
};

const isRefreshRequest = (config) => String(config?.url ?? '').includes('v1/refresh');

const refreshAccessToken = async () => {
  if (!refreshRequest) {
    refreshRequest = publicInstance
      .post(`v1/refresh`, {}, { withCredentials: true })
      .then(({ data }) => {
        const accessToken = data?.results?.accessToken;
        const profile_id = data?.results?.profile_id;

        if (!accessToken) {
          throw new Error('No accessToken in refresh response');
        }

        const nextAuth = {
          ...currentAuth,
          profile_id,
          accessToken,
        };

        updateAuthSession(nextAuth);

        return accessToken;
      })
      .finally(() => {
        refreshRequest = null;
      });
  }

  return refreshRequest;
};

instance.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};

  if (!config.headers.Authorization && currentAuth?.accessToken) {
    config.headers.Authorization = `Bearer ${currentAuth.accessToken}`;
  }

  return config;
});

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const prevRequest = error?.config;
    const status = error?.response?.status;

    if (
      (status === 401 || status === 403)
      && prevRequest
      && !prevRequest._retry
      && !isRefreshRequest(prevRequest)
    ) {
      prevRequest._retry = true;

      const accessToken = await refreshAccessToken();
      prevRequest.headers.Authorization = `Bearer ${accessToken}`;

      return instance(prevRequest);
    }

    return Promise.reject(error);
  }
);

export const getCampaigns = async (params) => {
  const { props, auth } = params;
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/campaigns`, { ...props, headers });
  return data;
};

export const getBrands = async (params) => {
  const { props, auth } = params;
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/brands`, { ...props, headers });
  return data;
};

export const getConsents = async (params) => {
  const { props, auth } = params;
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/consents`, { ...props, headers });
  return data;
};

export const getUsers = async (params) => {
  const { props, auth } = params;
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/users`, { ...props, headers });
  return data;
};

export const getContactTypes = async (params) => {
  const { props, auth } = params;
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/contact-types`, { ...props, headers });
  return data;
};

export const getProfiles = async (params) => {
  const { props, auth } = params;
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/profiles`, { ...props, headers });
  return data;
};

export const getContactsList = async (contact_list_id, auth) => {
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/contacts-list/${contact_list_id}`, {
    headers,
  });
  return data;
};

export const getUser = async (user_id, auth) => {
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/user/${user_id}`, { headers });
  return data;
};

export const insertUser = async (
  auth,
  id,
  name,
  last_name,
  email,
  company,
  profile_id,
  cell_phone
) => {
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };

  const { data } = await instance.post(
    `v1/user`,
    JSON.stringify({
      id,
      name,
      last_name,
      email,
      company,
      profile_id,
      cell_phone,
    }),
    { headers }
  );

  return data;
};

export const getLists = async (params) => {
  const { props, auth } = params;
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/contacts-lists`, { ...props, headers });
  return data;
};

export const refreshContactsListCount = async (contactListId, auth) => {
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.post(
    `v1/contacts-list/${contactListId}/refresh-contacts-count`,
    {},
    { headers }
  );
  return data;
};

export const getJourneys = async (params) => {
  const { props, auth } = params;
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/journeys`, { ...props, headers });
  return data;
};

export const getJourneyGroups = async (params) => {
  const { props, auth } = params;
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/journey-groups`, { ...props, headers });
  return data;
};

export const insertContactsList = async (
  auth,
  id,
  brand_id,
  name,
  contact_type_id
) => {
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };

  const { data } = await instance.post(
    `v1/contacts-list`,
    JSON.stringify({
      id,
      brand_id,
      name,
      contact_type_id,
    }),
    { headers }
  );

  return data;
};

export const insertCampaign = async (auth, id, brand_id, name) => {
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };

  const { data } = await instance.post(
    `v1/campaign`,
    JSON.stringify({
      id,
      brand_id,
      name,
    }),
    { headers }
  );

  return data;
};

export const getCampaign = async (campaign_id, auth) => {
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/campaign/${campaign_id}`, { headers });
  return data;
};

export const sendTestEmail = async (auth, id, test_emails) => {
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };

  const { data } = await instance.post(
    `v1/send-test-email`,
    JSON.stringify({
      id,
      test_emails,
    }),
    { headers }
  );

  return data;
};

export const activateEmail = async (id, active) => {
  const { data } = await instance.post(
    `v1/email-activate`,
    JSON.stringify({
      id,
      active,
    })
  );

  return data;
};

export const insertEmail = async (
  auth,
  id,
  active,
  brand_id,
  campaign_id,
  name,
  subject,
  sender_name,
  sender_email,
  reply_to,
  date_sent_from,
  date_sent_until,
  test_emails,
  contact_list_id,
  journey_id,
  html,
  resend_days,
  resend_subject
) => {
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };

  const { data } = await instance.post(
    `v1/email`,
    JSON.stringify({
      id,
      active,
      brand_id,
      campaign_id,
      name,
      subject,
      sender_name,
      sender_email,
      reply_to,
      date_sent_from,
      date_sent_until,
      test_emails,
      contact_list_id,
      journey_id,
      html,
      resend_days,
      resend_subject,
    }),
    { headers }
  );

  return data;
};

export const getEmails = async (params) => {
  const { props, auth } = params;
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/emails`, { ...props, headers });
  return data;
};

export const getEmail = async (email_id, auth) => {
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/email/${email_id}`, { headers });
  return data;
};

export const getEmailContacts = async (params) => {
  const { idEmail: email_id, props, auth } = params;
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/email-contacts/${email_id}`, {
    ...props,
    headers,
  });
  return data;
};

export const getContacts = async (params) => {
  const { props, auth } = params;
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/contacts`, { ...props, headers });
  return data;
};

export const getContactEmailTimeline = async (contactId, auth) => {
  const headers = {
    Authorization: `Bearer ${auth?.accessToken}`,
  };
  const { data } = await instance.get(`v1/contact/${contactId}/email-timeline`, {
    headers,
  });
  return data;
};

const buildDashboardHeaders = (auth) => {
  if (!auth?.accessToken) {
    return {};
  }
  return {
    Authorization: `Bearer ${auth.accessToken}`,
  };
};

const DASHBOARD_PREFIX = 'v1/dashboard';

export const getDashboardContacts = async ({ params = {}, auth }) => {
  const headers = buildDashboardHeaders(auth);
  const { data } = await instance.get(`${DASHBOARD_PREFIX}/contacts`, {
    params,
    headers,
  });
  return data;
};

export const getDashboardPets = async ({ params = {}, auth }) => {
  const headers = buildDashboardHeaders(auth);
  const { data } = await instance.get(`${DASHBOARD_PREFIX}/pets`, {
    params,
    headers,
  });
  return data;
};

export const getDashboardEmailContacts = async ({ params = {}, auth }) => {
  const headers = buildDashboardHeaders(auth);
  const { data } = await instance.get(`${DASHBOARD_PREFIX}/email-contacts`, {
    params,
    headers,
  });
  return data;
};

export const login = async (email, password) => {
  const { data } = await publicInstance.post(
    `v1/login`,
    JSON.stringify({ email, password }),
    { withCredentials: true } // 👈 clave para que el navegador acepte el Set-Cookie
  );
  return data;
};

export const checkOTP = async (otp) => {
  const { data } = await publicInstance.post(`v1/check-otp`, JSON.stringify({ otp }));
  return data;
};

export const passwordRecovery = async (email) => {
  const { data } = await publicInstance.post(
    `v1/user-password-recovery`,
    JSON.stringify({ email })
  );
  return data;
};

export const passwordReset = async (hash, password) => {
  const { data } = await publicInstance.post(
    `v1/user-password-reset`,
    JSON.stringify({ hash, password })
  );
  return data;
};

export const logout = async (auth) => {
  const headers = auth?.accessToken
    ? { Authorization: `Bearer ${auth.accessToken}` }
    : {};

  const { data } = await instance.post(`v1/logout`, {}, {
    headers,
    withCredentials: true,
  });
  return data;
};

export const refresh = async () => {
  const { data } = await publicInstance.post(
    `v1/refresh`,
    {},
    { withCredentials: true } // ✅ ahora viaja la cookie jwt_refresh
  );
  return data;
};
